import { selectIntentCards } from '../../selectors/menu';
import { DEFAULT_INTENT_CARDS } from '../../constants/intents';
import {
  AgentInterceptionType,
  messagingActions,
  TextFrameTransmissionMessage,
} from '../../reducers/messagingSlice';
import { Intents } from '../../types/intent';
import useSendMessageHooks from '../useSendMessage/useSendMessage.hooks';
import useSlashCommandReminder from '../useSlashCommandReminder.hooks';
import useAgentInterception from '../agentInterception/useAgentInterception.hooks';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectFeatureFlagOCBReadback } from '../../redux/features/config/config.selector';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import { selectIsAgentActionBlocked } from '../../redux/features/agentMode/agentMode.selector';
import logger from '../../utils/logger';

const useReadback = () => {
  const dispatch = useAppDispatch();
  const { showSlashCommandReminder } = useSlashCommandReminder();
  const { sendReadBackMessage } = useSendMessageHooks();
  const { sendAgentInterception } = useAgentInterception();
  const isOCBReadbackEnabled = useAppSelector(selectFeatureFlagOCBReadback);
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  const isBrandMenu = getMenuUnificationFeatureFlag();
  const intentCardsFromBM = useAppSelector(selectIntentCards);
  const INTENT_CARDS = intentCardsFromBM ?? DEFAULT_INTENT_CARDS;

  const sendOCBTTSMessage = (message: string) => {
    const payload: Partial<TextFrameTransmissionMessage> = {
      data: {
        payload: message,
        status: 'final',
        metadata: { intent: 'read-back' },
      },
    };
    dispatch(messagingActions.sendMessage(payload as any));
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.TTS_SENT,
        description: message,
      },
    });
  };

  const handleReadBack = ({
    timePressed,
    forceRegularReadback = false,
  }: {
    timePressed: number;
    forceRegularReadback?: boolean;
  }) => {
    // HITL-1606: outside Intercept the agent may not speak or send. Guarded
    // HERE, at the send, not only in useAgentInterception: this path dispatches
    // the outbound frame FIRST and stamps the interception after, so refusing
    // the interception alone would still let the customer hear it. Reachable by
    // keyboard with no button to disable, so a refusal is expected, not a defect.
    if (isAgentActionBlocked) {
      logger.debug({
        message: 'Readback refused: the agent is not in Intercept mode',
      });
      return;
    }
    const readbackCard = INTENT_CARDS.find(
      (intent) => intent.intent === Intents.readback
    );
    const { intent, messages = [] } = readbackCard ?? {};

    if (!forceRegularReadback) {
      // HITL-1253: readback must not auto-populate or re-populate the
      // end-of-order upsell in the blue reminder.
      showSlashCommandReminder({ excludeUpsell: true });
    }

    if (isOCBReadbackEnabled && isBrandMenu && !forceRegularReadback) {
      sendOCBTTSMessage(messages?.[0] ?? '');
    } else {
      if (forceRegularReadback) {
        logger.debug({
          message:
            'Readback: force regular readback - sending standard readback request (OCB TTS bypassed)',
          intent,
          timePressed,
        });
      }
      sendReadBackMessage(intent as string, timePressed);
    }
  };

  return {
    handleReadBack,
  };
};

export default useReadback;
